import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { ValidationErrors, Validators } from '@angular/forms';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { PhoneNumberDirection } from '@app/core/interfaces/c5/phone-number-direction';
import { ScreenNumber } from '@app/core/interfaces/screen-number';
import { SearchEnterprisePhoneNumbersParameters } from '@app/core/interfaces/search-enterprise-phone-numbers-parameters';
import { UpdateDialogComponent } from '@app/shared/dialog/update-dialog-component';
import { PhoneNumberFormModel } from '@app/shared/models/phone-number-form-model';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { EnterprisePhoneNumberService } from '@app/shared/services/enterprise-phone-number.service';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { FadeInOut } from '@app/utils/angular-animations';
import { Identifier, toMap } from '@app/utils/common';
import { PortalError } from '@app/utils/error/portal-error';
import { NumberValidator, RequiredValidator } from '@app/utils/form-mapping/helpers/default-validators';
import { isPhoneNumberBillable } from '@app/utils/is-phone-number-billable';
import { loggerFor } from '@app/utils/logger';
import { NumberMap } from '@app/utils/number-map';
import { isBlank } from '@app/utils/string';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { from, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { FormGroupTyped } from 'src/types/typed-forms';

export interface UpdatePhoneNumberInput {
  allowOutgoingDirection?: boolean;

  /**
   * If the select box should support creation of an internal number
   */
  allowAddingInternalNumber?: boolean;

  /**
   * Allow adding an out-only number See https://redmine.neotel.at/issues/39940
   */
  allowAddingServiceNumber?: boolean;

  /**
   * Needed to filter screen numbers out that do not belong to this screen number ID
   */
  siteId?: number;

  /**
   * To use the component without the <form> tag, as a part of another form.
   */
  noFormTag?: boolean;
  screenNumberFilter?: (screenNumber: ScreenNumber) => boolean;
  updateFunction?: (phoneNumber: PhoneNumber) => Promise<void>;
  userId?: number;
}

@UntilDestroy()
@Component({
  selector: 'portal-update-phone-number',
  templateUrl: './update-phone-number.component.html',
  styles: [],
  animations: [FadeInOut],
  standalone: false,
})
export class UpdatePhoneNumberComponent<T = UpdatePhoneNumberInput>
  extends UpdateDialogComponent<T, void>
  implements OnInit, UpdatePhoneNumberInput
{
  @Input()
  allowOutgoingDirection: boolean = false;

  /**
   * Allow one way binding from internal phone number to extensions
   */
  @Input()
  extensionValue: any;

  /**
   * If the select box should support creation of an internal number
   */
  @Input()
  allowAddingInternalNumber: boolean = true;

  /**
   * Allow adding an out-only number See https://redmine.neotel.at/issues/39940
   */
  @Input()
  allowAddingServiceNumber: boolean = false;

  /**
   * Needed to filter screen numbers out that do not belong to this screen number ID
   */
  @Input()
  siteId?: number;

  /**
   * To use the component without its own html `<form>` tag.
   * `true` means that the template of the component will be used in the html form you provide.
   */
  @Input()
  noFormTag: boolean = false;

  @Input()
  screenNumberFilter?: (screenNumber: ScreenNumber) => boolean;

  @Input()
  updateFunction: (phoneNumber: PhoneNumber) => Promise<void>;

  @Input()
  userId: number = -1;

  /**
   * In case you do not want the form-control to be validated for required value.
   * Default is `YES`.
   */
  @Input()
  useRequiredValidator: 'YES' | 'NO' | 'ONLY_WHEN_HAVING_BILLABLE_NUMBERS' = 'YES';

  private _useRequiredValidator = true;

  @Output() formValues = new EventEmitter<{
    internal: string;
    screenNumberId: Identifier;
    errors: ValidationErrors;
  }>();

  public availableOnlyAsOutgoingNumber: boolean = false;
  public form: FormGroupTyped<PhoneNumberFormModel> = PhoneNumberFormModel.formGroup();

  /**
   * The screen numbers items the user can select
   */
  public items: TypeaheadEntry<Identifier>[];

  /**
   * Indicates that the phone number is currently being validated
   */
  public pending: boolean = false;

  /**
   * Indicates that the phone number is currently being saved
   */
  public saving: boolean = false;

  /**
   * Warning that is shown to the user
   */
  public warning: string | null = null;

  /**
   * Value of the internal typeahead entry (is used to set the default value of the screen number input)
   */
  protected internalValue = '-1';

  protected resetValue: PhoneNumberFormModel;
  protected screenNumberMap: NumberMap<ScreenNumber> = {};

  private logger = loggerFor('SharedUpdatePhoneNumber');

  constructor(
    protected enterprisePhoneNumberService: EnterprisePhoneNumberService,
    protected enterpriseContextService: EnterpriseContextService,
    protected errorTransformerService: ErrorTransformerService,
    protected phoneNumberPipe: PhoneNumberPipe,
    protected translateService: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.form = PhoneNumberFormModel.formGroup(
      {},
      {
        asyncValidators: [this.checkNumberAvailability.bind(this)],
      }
    );
    this.form.controls['screenNumberId'].setValidators([RequiredValidator]);

    this.initializePhoneNumbers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.siteId != null &&
      changes.siteId.currentValue !== changes.siteId.previousValue &&
      !changes.siteId.firstChange
    ) {
      // reapplying the screen number filter
      this.enterpriseContextService.screenNumbers.data$.pipe(take(1)).subscribe((screenNumbers) => {
        this.populateScreenNumbers(screenNumbers);
        this.updateInternalControl(this.resetValue.screenNumberId);
      });
    }
    if ('extensionValue' in changes) {
      const change = changes.extensionValue;
      const internalFormControl = this.form.get('internal');
      const previousValueWasTheSame = change.previousValue === internalFormControl.value;
      const internalIsBlank = isBlank(internalFormControl.value);

      if (previousValueWasTheSame || internalIsBlank) {
        internalFormControl.setValue(changes.extensionValue.currentValue);
        internalFormControl.updateValueAndValidity();
      }
    }
  }

  public submit() {
    const model = this.mapFormToPhoneNumber();

    this.resetValue = this.form.value;

    this.handleSubmit(from(this.updateFunction(model)));
  }

  /**
   * Async validation
   * @param control
   */
  protected checkNumberAvailability(
    control: FormGroupTyped<PhoneNumberFormModel>
  ): Promise<ValidationErrors> | Observable<ValidationErrors> {
    let errors: Promise<ValidationErrors> | Observable<ValidationErrors> = of(null);

    if (control.controls.screenNumberId !== undefined && control.controls.internal !== undefined) {
      this.warning = null;

      /*
       * modal.entry.screenNumberId = (modal._screenNumber ? modal._screenNumber.id : null);
       * modal._screenNumber.exactMatch ? modal.entry.internal = undefined : false;
       * modal.vm.checkNumberAvailability(modal, modal._screenNumber, modal.entry)
       */

      const screenNumber = this.screenNumberMap[control.controls.screenNumberId.value];

      this.logger.info('Checking number availability', screenNumber);

      let cc;
      let ext;
      let internal;
      let ndc;
      let sn;

      // Clear previous errors before validation proceeds
      this.formValues.next({
        internal: this.form.controls.internal.value,
        screenNumberId: this.form.controls.screenNumberId.value,
        errors: null,
      });

      if (this.isScreenNumber(screenNumber)) {
        // In this case the user has selected a screen number
        if (control.controls.internal.value) {
          ext = control.controls.internal.value;
        } else {
          ext = '';
        }

        cc = screenNumber.cc;
        ndc = screenNumber.ndc;
        sn = screenNumber.sn + ext;
      } else {
        internal = control.controls.internal.value;
      }

      // It can happen that angular started validating already and then the validator of the internal
      // control is updated. In that case we need to check if one value is defined, if not then the
      // validation should not run
      if (internal || cc || ndc || sn) {
        errors = timer(250).pipe(
          switchMap(() =>
            this.validatePhoneNumber({
              cc,
              internal,
              ndc,
              sn,
            })
          )
        );
      } else {
        // If all fields are empty then the user just switched from a screen number to the 'internal' value
        // and we need to set the validators and update the validity
        if (this._useRequiredValidator) {
          control.controls.internal.setValidators([
            Validators.maxLength(10),
            RequiredValidator,
            NumberValidator,
          ]);
        } else {
          control.controls.internal.setValidators([Validators.maxLength(10), NumberValidator]);
        }

        control.controls.internal.updateValueAndValidity({
          onlySelf: true,
        });
      }
    }

    const screenNumberCtrlValue = control.controls.screenNumberId?.value;
    if (screenNumberCtrlValue == undefined || screenNumberCtrlValue === '') {
      let errors = null;
      if (this._useRequiredValidator) {
        errors = { required: 'screenNumberId' };
      }
      this.formValues.next({
        internal: this.form.controls.internal.value,
        screenNumberId: this.form.controls.screenNumberId.value,
        errors,
      });
    }

    return errors;
  }

  protected initializePhoneNumbers() {
    this.form.controls.screenNumberId.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((screenNumber) => this.updateInternalControl(screenNumber));

    this.enterpriseContextService.screenNumbers.data$
      .pipe(untilDestroyed(this))
      .subscribe((screenNumbers) => {
        this.populateScreenNumbers(screenNumbers);
        this.updateInternalControl(this.resetValue.screenNumberId);
        this.adaptFormControlValidation(screenNumbers);
      });
  }

  private adaptFormControlValidation(screenNumbers: ScreenNumber[]): void {
    if (this.useRequiredValidator === 'NO') {
      this._useRequiredValidator = false;
      this.form.controls['screenNumberId'].clearValidators();
    } else if (this.useRequiredValidator === 'ONLY_WHEN_HAVING_BILLABLE_NUMBERS') {
      const hasBillableNumbers = screenNumbers.some((pn) => isPhoneNumberBillable(pn));
      if (hasBillableNumbers) {
        this._useRequiredValidator = true;
        this.form.controls['screenNumberId'].setValidators([RequiredValidator]);
      } else {
        this._useRequiredValidator = false;
        this.form.controls['screenNumberId'].clearValidators();
      }
    } else {
      this._useRequiredValidator = true;
      this.form.controls['screenNumberId'].setValidators([RequiredValidator]);
    }
    this.form.controls['screenNumberId'].updateValueAndValidity();
    // call explicitly in order to emit the possible validation error to outside form
    this.checkNumberAvailability(this.form);
    this.form.updateValueAndValidity();
  }

  /**
   * Check if the given value is a screen number
   * @param value
   */
  protected isScreenNumber(value: any): value is ScreenNumber {
    return typeof value === 'object';
  }

  protected mapFormToPhoneNumber() {
    return {
      internal: this.form.value.internal,
      outgoing: this.availableOnlyAsOutgoingNumber,
      screenNumberId:
        typeof this.form.value.screenNumberId === 'number' ? this.form.value.screenNumberId : undefined,
    };
  }

  /**
   * Map the given screen numbers to typeahead entries
   * @param screenNumbers
   */
  protected populateScreenNumbers(screenNumbers: ScreenNumber[]) {
    this.screenNumberMap = toMap(screenNumbers, 'id');
    const items: TypeaheadEntry<Identifier>[] = [];

    for (const screenNumber of screenNumbers) {
      if (this.screenNumberIsAllowed(screenNumber)) {
        items.push({
          key: String(screenNumber.id),
          label: this.phoneNumberPipe.transform(screenNumber),
          meta: {
            isOutgoing: screenNumber.dir === PhoneNumberDirection.OUT,
            description: screenNumber.description,
          },
          value: screenNumber.id,
        });
      }
    }

    if (this.allowAddingInternalNumber === true) {
      items.unshift({
        key: 'internal',
        label: this.translateService.instant('phoneNumber.type.INTERNAL'),
        value: this.internalValue,
      });

      this.resetValue = PhoneNumberFormModel.from({
        // -1 is the value of the internal typeahead entry
        screenNumberId: this.internalValue,
      });
      this.form.patchValue(this.resetValue);
    } else {
      // items.unshift({
      //   key: 'internal',
      //   label: this.translateService.instant('phoneNumber.type.INTERNAL'),
      //   value: this.internalValue,
      // })
      // // select first screen number
      this.resetValue = PhoneNumberFormModel.from({
        screenNumberId: undefined, //items.length > 0 ? items[0].value : undefined,
      });
      this.form.patchValue(this.resetValue);
    }

    this.items = items;
    this.logger.debug('items have changed', items);
  }

  protected screenNumberIsAllowed(screenNumber: ScreenNumber): boolean {
    if (this.screenNumberFilter) {
      return this.screenNumberFilter(screenNumber);
    }

    const isOutgoing = screenNumber.dir === 'OUT';
    const isNoExactMatch = screenNumber.exactMatch === false;
    const isExactMatch = screenNumber.exactMatch === true;
    const isNoNetNumber = screenNumber.netNumber === false;
    const isExactMatchButNotAssigned = screenNumber.exactMatch === true && screenNumber.user == null;
    const hasNoSite = screenNumber.siteId == null;
    const siteMatches = screenNumber.siteId === this.siteId;

    let isAllowed: boolean =
      (isNoExactMatch || isExactMatchButNotAssigned) &&
      (isOutgoing && isExactMatch && isNoNetNumber) === false;

    if (this.allowAddingServiceNumber !== false) {
      isAllowed = isAllowed || isOutgoing;
    }

    if (this.siteId !== undefined) {
      isAllowed = isAllowed && (hasNoSite || siteMatches);
    }

    return isAllowed;
  }

  /**
   * Update the value, validation and enabled state of the internal form control
   * @param screenNumberId
   */
  protected updateInternalControl(screenNumberId: Identifier) {
    this.logger.info('Updating validation state of "internal". Chosen screen number is', screenNumberId);

    const screenNumber = this.screenNumberMap[screenNumberId];

    if (this.isScreenNumber(screenNumber)) {
      // Disable the internal control if the screen number is an exact match
      if (screenNumber.exactMatch === true) {
        this.form.controls.internal.clearValidators();
        this.form.controls.internal.disable();
        this.form.controls.internal.setValue(undefined);
      } else {
        this.form.controls.internal.setValidators([Validators.maxLength(10), NumberValidator]);
        this.form.controls.internal.enable();
      }
    } else {
      if (this._useRequiredValidator) {
        this.form.controls.internal.setValidators([
          Validators.maxLength(10),
          RequiredValidator,
          NumberValidator,
        ]);
      } else {
        this.form.controls.internal.setValidators([Validators.maxLength(10), NumberValidator]);
      }

      this.form.controls.internal.enable();
    }
  }

  protected validatePhoneNumber({
    cc,
    internal,
    ndc,
    sn,
  }: SearchEnterprisePhoneNumbersParameters): Observable<ValidationErrors | null> {
    return this.enterprisePhoneNumberService
      .search({
        cc,
        ndc,
        sn,
        internal,
      })
      .pipe(
        map((phoneNumbers) => {
          let errors: ValidationErrors = null;
          this.availableOnlyAsOutgoingNumber = false;
          const userId = this.userId;

          this.logger.info(
            `Found the following phone numbers (cc/ndc/sn/int=${cc}/${ndc}/${sn}/${internal}) for user ${userId}`,
            phoneNumbers
          );

          const inUsePhoneNumber = phoneNumbers.find(
            (num) => num.dir === PhoneNumberDirection.IN_OUT || num.dir === PhoneNumberDirection.IN
          );

          // Check if the user has this number already as outgoing
          const alreadyOwnNumber = phoneNumbers.find(
            (num) =>
              // (num.dir === PhoneNumberDirection.OUT || num.dir === PhoneNumberDirection.IN_OUT) &&
              num.userId === userId
          );

          // Error out of number is already assigned
          if (alreadyOwnNumber !== undefined) {
            errors = {
              custom: 'users.phoneNumbers.add.alreadyAdded',
            };
          } else if (inUsePhoneNumber !== undefined) {
            // The phone number is assigned to another user or service, check if it can be used as
            // outgoing. Using as outgoing is only allowed for external numbers currently
            if (internal == null && this.allowOutgoingDirection === true) {
              errors = null;
              this.availableOnlyAsOutgoingNumber = true;
              this.warning = 'availableOnlyAsOutgoingNumber';
            } else if (internal != null && this.siteId != null && inUsePhoneNumber.siteId !== this.siteId) {
              this.warning = 'numberExistsOnDifferentSite';
            } else {
              errors = {
                custom: 'users.phoneNumbers.add.alreadyInUse',
              };
            }

            this.logger.info(
              'Errors:',
              errors,
              '- already in use and can be added as outgoing:',
              this.availableOnlyAsOutgoingNumber
            );
          }

          this.formValues.next({
            internal: this.form.controls.internal.value,
            screenNumberId: this.form.controls.screenNumberId.value,
            errors,
          });

          return errors;
        }),
        catchError((error: PortalError) => {
          const description = this.errorTransformerService.describeError(error);

          return of({
            custom: description.details?.exception ?? description.title,
          });
        })
      );
  }
}
